import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { Button, classes } from './ui.tsx';
import { PLANS } from '@shared/constants/index.ts';

/** A week, the most often any one of these lines may come back. */
const SHOWN_AGAIN_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

interface StarterNudgeProps {
  /** Distinguishes this line's memory from every other one's. */
  id: string;
  /** The sentence, already translated: what Starter would do here. */
  message: string;
  className?: string;
  /**
   * Show it once and never again, rather than once a week: for a line that
   * is about one particular thing (this customer), which says nothing new
   * the second time.
   */
  once?: boolean;
}

/**
 * One quiet line pointing a company on the Free plan at Starter, beside the
 * work Starter would take off their hands — the payment screen, the money
 * still owed.
 *
 * The same rules as `UpgradeNudge`, held tighter, because Free is a plan
 * somebody chose and the product promised to keep useful:
 *
 *  - Free only, and the owner only — nobody else can change the plan.
 *  - At most once a week. The visit it first shows in is the only one that
 *    week; after that it stays out of the way until the week is up, however
 *    often the screen is opened.
 *  - Dismissible, and dismissed for good, per company and per line.
 *
 * Remembered in this browser only. A suggestion is not worth a server round
 * trip, and losing the memory costs at most one more showing.
 */
export function StarterNudge(props: StarterNudgeProps) {
  const { user, company } = useSession();
  // Nothing is read or written for anybody the line is not for, so a manager
  // opening the screen does not use up the owner's week.
  if (user?.role !== 'owner' || company?.effectivePlan !== 'free') return null;
  return <WeeklyLine {...props} storageKey={`cqb:starter-nudge:${company.id}:${props.id}`} />;
}

function WeeklyLine({ message, className, storageKey, once }: StarterNudgeProps & { storageKey: string }) {
  const { t } = useTranslation();
  // Decided once, when the screen first renders it, and written down then:
  // the line stays put while this screen is open, and the week starts now.
  const [visible, setVisible] = useState(() => decideOnce(storageKey, Boolean(once)));
  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    decidedThisLoad.set(storageKey, false);
    try {
      window.localStorage.setItem(storageKey, 'dismissed');
    } catch {
      // Hidden for this page only. Nothing anybody asked for has failed.
    }
  };

  return (
    <div
      className={classes(
        'flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm',
        'border-[color:var(--color-line)] bg-[color:var(--color-surface)]',
        className,
      )}
    >
      <p className="text-[color:var(--color-muted)]">{message}</p>
      <div className="flex items-center gap-2">
        <Link to="/settings#settings-billing" className="font-semibold text-[color:var(--color-brand)] underline">
          {t('starterNudge.cta', { plan: PLANS.starter.name })}
        </Link>
        <Button variant="ghost" onClick={dismiss}>
          {t('common.dismiss')}
        </Button>
      </div>
    </div>
  );
}

/**
 * What each line decided on this page load. The decision is made once per
 * load and then kept, so a screen opened twice in one visit agrees with
 * itself — and so React rendering a component twice in development does not
 * read its own "shown" mark and hide the line it just decided to show.
 */
const decidedThisLoad = new Map<string, boolean>();

/**
 * Whether this line may show during this visit: never once dismissed, and not
 * again within a week of the visit it last showed in. Showing it is recorded
 * here, so the next visit inside the week leaves it out.
 */
function decideOnce(storageKey: string, once = false): boolean {
  const decided = decidedThisLoad.get(storageKey);
  if (decided !== undefined) return decided;
  let visible = true;
  try {
    const stored = window.localStorage.getItem(storageKey);
    const lastShown = stored?.startsWith('shown:') ? Number(stored.slice('shown:'.length)) : NaN;
    if (stored === 'dismissed') visible = false;
    else if (once && Number.isFinite(lastShown)) visible = false;
    else if (Number.isFinite(lastShown) && Date.now() - lastShown < SHOWN_AGAIN_AFTER_MS) visible = false;
    else window.localStorage.setItem(storageKey, `shown:${Date.now()}`);
  } catch {
    // Storage refused: show it, without a memory, rather than never.
  }
  decidedThisLoad.set(storageKey, visible);
  return visible;
}
